import type { BillingPlanKey } from '../context/env-context';
import { isProduction } from './environment';

export const features = Object.freeze({
  CUSTOMER_GROUPS_AMEND: 'CUSTOMER_GROUPS_AMEND',
  TEAM_MEMBER_ROLE: 'TEAM_MEMBER_ROLE',
  MULTI_STOCK_LOCATION_SUPPORT: 'MULTI_STOCK_LOCATION_SUPPORT',
  STOCK_DISPLAY: 'STOCK_DISPLAY',
  QUOTING: 'QUOTING',
  INVOICING: 'INVOICING',
  PAYMENTS_VISIBILITY: 'PAYMENTS_VISIBILITY',
  WORKFLOWS: 'WORKFLOWS',
  ANALYTICS_NAV: 'ANALYTICS_NAV',
  ANALYTICS: 'ANALYTICS',
  THEMES: 'THEMES',
  SHIPPING: 'SHIPPING',
  COMPANIES: 'COMPANIES',
  API_ACCESS: 'API_ACCESS',
  DISCOUNTS: 'DISCOUNTS',
  DISCOUNTS_FREE_PRODUCT: 'DISCOUNTS_FREE_PRODUCT',
  DISCOUNTS_PRODUCT_LEVEL_AMOUNT_OFF: 'DISCOUNTS_PRODUCT_LEVEL_AMOUNT_OFF',
  DISCOUNTS_SHIPPING_REWARD: 'DISCOUNTS_SHIPPING_REWARD',
  DISCOUNTS_ADVANCED: 'DISCOUNTS_ADVANCED',
  ACCOUNTING: 'ACCOUNTING',
  XERO: 'XERO',
  QUICKBOOKS: 'QUICKBOOKS',
  FILE_UPLOAD: 'FILE_UPLOAD',
});

export type FeatureFlag = keyof typeof features;

const platforms = Object.freeze({
  SHOPIFY: 'shopify',
  SHOPIFY_PLUS: 'shopify_plus',
  BIGCOMMERCE: 'bigcommerce',
  WIX: 'wix',
  WOOCOMMERCE: 'woocommerce',
  MAGENTO: 'magento',
  CUSTOM: 'custom',
});

type Platform = (typeof platforms)[keyof typeof platforms];

const ACTIVE_PLAN_KEYS = [
  'development',
  'basic_free',
  'v2_basic',
  'v2_starter_plus',
  'v2_growth',
  'v2_pro',
  'enterprise',
  'enterprise_onboarding',
  'custom',
] as const satisfies readonly BillingPlanKey[];

export type ActivePlanKey = (typeof ACTIVE_PLAN_KEYS)[number];

const PLAN_ORDER: Record<ActivePlanKey, number> = {
  development: -1,
  // Free plan
  basic_free: 0,
  // Starter Plan
  v2_basic: 1,
  // Starter Plus Plan
  v2_starter_plus: 2,
  // Growth Plan
  v2_growth: 3,
  // Pro Plan
  v2_pro: 4,
  // Custom Plans
  enterprise: 5,
  enterprise_onboarding: 6,
  custom: 7,
};

export function activePlanMeetsOrExceeds(
  activePlan: ActivePlanKey,
  minPlan: ActivePlanKey,
): boolean {
  return PLAN_ORDER[activePlan] >= PLAN_ORDER[minPlan];
}

function isActiveBillingPlanKey(billingPlanKey?: BillingPlanKey): billingPlanKey is ActivePlanKey {
  if (!billingPlanKey) {
    return false;
  }
  return (ACTIVE_PLAN_KEYS as readonly BillingPlanKey[]).includes(billingPlanKey);
}

export function getActiveBillingPlanKey(
  billingPlanKey?: BillingPlanKey,
): ActivePlanKey | undefined {
  return isActiveBillingPlanKey(billingPlanKey) ? billingPlanKey : undefined;
}

type Feature = {
  enabledForProduction?: boolean;
  siteIdOverrides?: string[];
  disabledPlatforms?: Platform[];
  requiredPlan?: ActivePlanKey;
};

const featureState = Object.freeze({
  // Hide team member invite role - currently does not do anything
  [features.TEAM_MEMBER_ROLE]: {
    enabledForProduction: false,
  },
  [features.MULTI_STOCK_LOCATION_SUPPORT]: {
    enabledForProduction: true,
    requiredPlan: 'v2_growth',
  },
  [features.QUOTING]: {
    requiredPlan: 'v2_pro',
  },
  [features.INVOICING]: {
    requiredPlan: 'v2_growth',
  },
  [features.PAYMENTS_VISIBILITY]: {
    enabledForProduction: true,
    disabledPlatforms: [
      platforms.BIGCOMMERCE,
      platforms.MAGENTO,
      platforms.WIX,
      platforms.WOOCOMMERCE,
    ],
  },
  [features.WORKFLOWS]: {
    requiredPlan: 'v2_growth',
  },
  [features.ANALYTICS_NAV]: {
    enabledForProduction: false,
  },
  [features.ANALYTICS]: {
    requiredPlan: 'v2_pro',
  },
  [features.THEMES]: {
    requiredPlan: 'v2_pro',
  },
  [features.SHIPPING]: {
    requiredPlan: 'v2_basic',
  },
  [features.COMPANIES]: {
    enabledForProduction: false,
  },
  [features.API_ACCESS]: {
    requiredPlan: 'v2_pro',
  },
  [features.DISCOUNTS]: {
    requiredPlan: 'v2_basic',
  },
  [features.DISCOUNTS_FREE_PRODUCT]: {
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS_PRODUCT_LEVEL_AMOUNT_OFF]: {
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS_SHIPPING_REWARD]: {
    requiredPlan: 'v2_growth',
  },
  [features.DISCOUNTS_ADVANCED]: {
    requiredPlan: 'v2_pro',
  },
  [features.ACCOUNTING]: {
    enabledForProduction: true,
    disabledPlatforms: [platforms.BIGCOMMERCE, platforms.MAGENTO, platforms.WOOCOMMERCE],
  },
  [features.XERO]: {
    enabledForProduction: true,
    requiredPlan: 'v2_pro',
    disabledPlatforms: [
      platforms.BIGCOMMERCE,
      platforms.MAGENTO,
      platforms.WIX,
      platforms.WOOCOMMERCE,
    ],
  },
  [features.QUICKBOOKS]: {
    enabledForProduction: true,
    requiredPlan: 'v2_pro',
    disabledPlatforms: [platforms.BIGCOMMERCE, platforms.MAGENTO, platforms.WOOCOMMERCE],
  },
  [features.FILE_UPLOAD]: {
    requiredPlan: 'v2_growth',
  },
}) as Readonly<Record<FeatureFlag, Feature>>;

export function featureEnabled(
  featureFlag: FeatureFlag,
  siteId: string,
  platform?: Platform,
): boolean {
  const feature = featureState[featureFlag];

  if (feature?.siteIdOverrides?.includes(siteId)) {
    return true;
  }

  if (feature?.siteIdOverrides !== undefined && isProduction) {
    return false;
  }

  if (platform && feature?.disabledPlatforms?.includes(platform)) {
    return false;
  }

  if (feature?.enabledForProduction === false && isProduction) {
    return false;
  }

  return true;
}

export function featureRestricted(
  featureFlag: FeatureFlag,
  siteBillingPlan?: ActivePlanKey,
): boolean {
  const feature = featureState[featureFlag];

  if (!feature?.requiredPlan) {
    return false;
  }
  // if the feature requires a plan, check if we even have a billing plan
  if (!siteBillingPlan) {
    return true;
  }

  return !activePlanMeetsOrExceeds(siteBillingPlan, feature.requiredPlan);
}
